import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { GridEventListener, GridRowParams, GridValidRowModel } from '@mui/x-data-grid';

import { VatixError } from 'components/Error/types';

import { useStore } from 'utils/hooks/store';

import routes from 'core/routes';

import Grid from 'components/Grid';

import { ColumnDefinition } from 'components/Grid/types';
import { GroupStatusOperator } from 'components/Grid/operators/Status';

import GroupStatusChip from 'components/Chips/GroupStatusChip';

import { listRenderer } from 'components/Grid/renderers/GridListRenderer';
import { UuidableName } from 'utils/api/types';
import { UserLicensesResponse } from 'utils/api/modules/user/user.types';

import { env } from 'config';

import UserCell from '../UserCell';
import UserRoleCell from '../UserRoleCell';
import UserProductsCell from '../UserProductsCell';

import UserRoleOperators from './operators/UserRolesOperator';
import ProductLicencesOperator from './operators/ProductLicencesOperator';

import { DimInactiveRows } from './styles';

const dimInactiveCell = ({ row }: { row: GridValidRowModel }): string => (row.isActive ? '' : 'cell--inactive');

const basicColumns = [
  {
    field: 'name',
    headerName: 'Name',
    valueGetter: (_value: unknown, row: GridValidRowModel) => ({ name: row.name, uuid: row.uuid }),
    renderCell: ({ value }: { value: { name: string; uuid: string } }) => (
      <UserCell name={value.name} uuid={value.uuid} />
    ),
    cellClassName: dimInactiveCell,
    nativeFilter: true,
    minWidth: 250,
    flex: 2,
  },
  {
    field: 'isActive',
    headerName: 'Status',
    renderCell: ({ value }: { value: boolean }) => <GroupStatusChip isActive={value} />,
    filterOperators: GroupStatusOperator(),
    // Query params aren't decamelized, so `true` would emit ?isActive= and the filterset drops it.
    nativeFilter: 'is_active',
    sortable: false,
    minWidth: 130,
    flex: 0.6,
    valueFormatter: (params: { value: boolean } | null) => (params ? (params.value ? 'Active' : 'Inactive') : ''),
  },
  {
    field: 'role',
    headerName: 'Role',
    renderCell: ({ value }: { value: string }) => <UserRoleCell role={value} />,
    filterOperators: UserRoleOperators,
    cellClassName: dimInactiveCell,
    nativeFilter: true,
    flex: 1,
  },
  {
    field: 'groups',
    headerName: 'Teams',
    renderCell: listRenderer(
      ({ value }) => <p>{(value.value as UuidableName).name}</p>,
      3,
      (values: unknown[]) => values.map((value) => (value as UuidableName).name).join(', ')
    ),
    cellClassName: dimInactiveCell,
    nativeFilter: true,
    minWidth: 150,
    sortable: false,
  },
  {
    field: 'licenses',
    headerName: 'Product licences',
    renderCell: ({ value }: { value: UserLicensesResponse }) => <UserProductsCell licenses={value} />,
    filterOperators: ProductLicencesOperator,
    cellClassName: dimInactiveCell,
    nativeFilter: true,
    minWidth: 300,
    flex: 2,
    sortable: false,
  },
] as unknown as ColumnDefinition[];

const UserList: React.FunctionComponent<{
  onRefreshCallback: (callback: () => void) => void;
  onError: (error: VatixError) => void;
}> = ({ onError, onRefreshCallback }) => {
  const { routing } = useStore();
  const onRowClick: GridEventListener<'rowClick'> = (user: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.settings.users.details, { userId: user.row.uuid }));
  };

  return (
    <DimInactiveRows>
      <Grid
        basicColumns={basicColumns}
        filters={[{ field: 'isActive', operator: 'is', value: 'active' }]}
        onError={onError}
        onRowClick={onRowClick}
        dataURL={`${env.REACT_APP_ADMIN_API_URL}users/`}
        onRefreshCallback={onRefreshCallback}
        sortBy={{ field: 'name', sort: 'asc' }}
        disableColumnSelector
        onlyBasicExport={false}
      />
    </DimInactiveRows>
  );
};

export default observer(UserList);
